<template>
  <div class="search-page-pagination-wrapper">
    <div class="search-page-pagination-container header">
      <div class="search-page-pagination-container-area">
        <div class="total-count">{{ totalCountHeader }}</div>
        <div class="total-pages hide">{{ paginationHeader }}</div>
      </div>
    </div>
    <div class="search-page-pagination-container pagination">
      <div class="search-page-pagination-container-area total-perPage">
        <select
          :value="searchPageStore.searchPageState.query.perPage"
          @blur="handleOnBlur"
          @change="
            handlePageSizeChange(
              Number(($event.target as HTMLSelectElement).value)
            )
          "
        >
          <option
            v-for="option in searchPageStore.searchPageState.query
              .perPageOptions"
            :value="option"
            :key="option"
            :selected="option === searchPageStore.searchPageState.query.perPage"
          >
            {{ option }}
          </option>
        </select>
        <span>items per page</span>
        <div></div>
      </div>
      <div
        class="search-page-pagination-container-area page-navigation"
        v-if="
          searchPageStore.searchPageState.query.pageTotal > 1 &&
          showPaginationComponent
        "
      >
        <button
          v-if="showPreviousPageArrow"
          class="pagination:number arrow"
          @click="paginationPrevious"
        >
          &lt;
        </button>
        <button
          v-if="currentPageNumber > 1"
          @click="handlePageNumberClick(1)"
          :class="{
            active: isPageActive(1),
          }"
        >
          1
        </button>
        <div class="pagination-dots" v-if="currentPageNumber > 3">...</div>
        <button
          v-for="(item, index) in pageNumberData.filter((item: any) => item.expression)"
          :key="index"
          @click="handlePageNumberClick(item.value)"
          :class="{
            active: isPageActive(item.value),
          }"
        >
          {{ item.value }}
        </button>
        <div
          class="pagination-dots"
          v-if="currentPageNumber < currentPageTotal - 2"
        >
          ...
        </div>
        <button
          v-if="currentPageTotal > 1 && currentPageNumber < currentPageTotal"
          @click="handlePageNumberClick(currentPageTotal)"
          :class="{
            active: isPageActive(currentPageTotal),
          }"
        >
          {{ currentPageTotal }}
        </button>
        <button
          v-if="showNextPageArrow"
          class="pagination:number arrow"
          @click="paginationNext"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useSearchPageStore } from "@/store/store/searchPageStore";

const searchPageStore = useSearchPageStore();

const pageNumberData = computed(() => {
  return [
    {
      expression: false,
      value: 1,
      selected: currentPageNumber.value === 1,
    },
    {
      expression: currentPageNumber.value - 2 > 1,
      value: currentPageNumber.value - 2,
      selected: currentPageNumber.value === currentPageNumber.value - 2,
    },
    {
      expression: currentPageNumber.value - 1 > 1,
      value: currentPageNumber.value - 1,
      selected: currentPageNumber.value === currentPageNumber.value - 1,
    },
    {
      expression: true,
      value: currentPageNumber.value,
      selected: true,
    },
    {
      expression: currentPageNumber.value + 1 < currentPageTotal.value,
      value: currentPageNumber.value + 1,
      selected: currentPageNumber.value === currentPageNumber.value + 1,
    },
    {
      expression: currentPageNumber.value + 2 < currentPageTotal.value,
      value: currentPageNumber.value + 2,
      selected: currentPageNumber.value === currentPageNumber.value + 2,
    },
  ];
});

const currentPageNumber = computed<number>(
  () => searchPageStore.searchPageState.query.pageNumber
);

const currentPageTotal = computed<number>(
  () => searchPageStore.searchPageState.query.pageTotal
);

const showNextPageArrow = computed<boolean>(
  () =>
    searchPageStore.searchPageState.query.pageNumber <
    searchPageStore.searchPageState.query.pageTotal
);

const showPreviousPageArrow = computed<boolean>(
  () => searchPageStore.searchPageState.query.pageNumber > 1
);

function isPageActive(pageNumber: number) {
  return searchPageStore.searchPageState.query.pageNumber === pageNumber;
}

function handleOnBlur() {
  // set the currently selected
}

function handlePageNumberClick(pageNumber: number) {
  searchPageStore.handlePageNumberClick(pageNumber);
}

function handlePageSizeChange(pageSize: number) {
  // check if the page size would result in only 1 page

  searchPageStore.handlePageSizeChange(pageSize);
}

function paginationNext() {
  searchPageStore.paginationNext();
}

function paginationPrevious() {
  searchPageStore.paginationPrevious();
}

const showPaginationComponent = computed<boolean>(
  () => searchPageStore.searchPageState.query.pageTotal > 1
);

const paginationEnabled = computed<boolean>(
  () => searchPageStore.searchPageState.query.paginationEnabled
);

const totalCountHeader = computed<string>(() => {
  if (searchPageStore.searchPageState.loading) {
    return "";
  }

  return (
    `Found ${searchPageStore.searchPageState.query.totalCount} Result` +
    `${searchPageStore.searchPageState.query.totalCount != 1 ? "s" : ""}`
  );
});

const paginationHeader = computed<string>(() => {
  if (!paginationEnabled.value) {
    return "";
  }
  return `Page ${searchPageStore.searchPageState.query.pageNumber} of
        ${searchPageStore.searchPageState.query.pageTotal}`;
});
</script>

<style scoped>
.hide {
  display: none;
}

.search-page-pagination-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
}

.search-page-pagination-container {
  display: flex;
  flex-direction: row;
}

.search-page-pagination-container-area {
  display: flex;
}

.search-page-pagination-container.header {
  margin-bottom: 0;
  align-items: flex-end;
}

.search-page-pagination-container-area.total-perPage {
  display: flex;
  align-items: center;
}
.search-page-pagination-container-area.total-perPage select {
  max-width: fit-content;
  height: 34px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #fff;
  color: #000;
  text-align: center;
  font-family: Montserrat;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
  padding: 0px;
  border: 2px solid #ffffff;
  min-width: 47px;
}

.search-page-pagination-container-area.total-perPage span {
  color: #000;
  text-align: right;
  font-family: Montserrat;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: none;
  padding-bottom: 0;
  margin-left: 0.5em;
}

.search-page-pagination-container-area.page-navigation {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.search-page-pagination-container.pagination {
  display: flex;
  flex-direction: row;
  column-gap: 29px;
  /* margin-bottom: 5px; */
}
.search-page-pagination-container-area.page-navigation button {
  width: 29px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 6px;
  color: #000000;
  cursor: pointer;
  border-color: #ffffff;
  background: #fff;
  /* margin-bottom: 10px; */
}

.search-page-pagination-container-area.page-navigation .pagination-dots {
  align-self: center;
  margin: 10px 10px 0 5px;
  font-size: 15px;
}
.search-page-pagination-container-area.page-navigation button > * {
  color: #000;
  text-align: right;
  font-family: Montserrat;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
}
.search-page-pagination-container-area.page-navigation button.active {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}
.pagination\:number.arrow {
  font-weight: 700;
}
</style>
